import axios from "axios";
import { getAccessToken } from "@/lib/safeStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  // NOTE: withCredentials must be FALSE because the Emergent ingress sets
  // `Access-Control-Allow-Origin: *` on every response, which combined with
  // `Allow-Credentials: true` is forbidden by the CORS spec — browsers (Firefox,
  // Safari, strict Chrome) silently reject the response and axios sees an empty
  // error. We use Bearer tokens via localStorage (centralised in
  // /app/frontend/src/lib/safeStorage.js) instead of cookies, so this is fine.
  // Trade-off: refresh-token cookie flow is disabled; users re-login every 24h.
  withCredentials: false,
  // 5 minutes — large multipart uploads from Android on weak 4G/3G regularly
  // exceed the previous 60s ceiling. Backend caps body at 30MB and the
  // frontend now compresses photos to ~2MB before send so this is generous.
  timeout: 300000,
});

// Attach Authorization header from centralised storage (sanitised + SSR-safe).
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

export default api;

export const fileUrl = (path) => {
  if (!path) return "";
  // iter 96g — backend now returns HMAC-signed URLs (`?sig=...&exp=...`)
  // that live 7 days. If the path already carries a signature, use it
  // verbatim; otherwise fall back to the legacy `?auth=<jwt>` scheme so
  // stale URLs in older React state / bookmarks keep working during the
  // transition period.
  const alreadySigned = path.includes("sig=") && path.includes("exp=");
  const abs = path.startsWith("/api/") ? `${BACKEND_URL}${path}` : `${API_BASE}/files/${path}`;
  if (alreadySigned) return abs;
  const token = getAccessToken();
  const sep = abs.includes("?") ? "&" : "?";
  return `${abs}${sep}auth=${encodeURIComponent(token)}`;
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
